import { Head, Link } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/components/ui/PageHeader'
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import { Badge } from '@/components/catalyst/badge'
import { ClipboardDocumentListIcon, PlusIcon } from '@heroicons/react/24/outline'

// ─── Types ──────────────────────────────────────

interface VpoRow {
  id: number
  vendor_name: string | null
  vendor_po_number: string | null
  vendor_po_status: string | null
  state: string | null
  currency: string | null
  vendor_order_date: string | null
  vendor_invoice_number: string | null
  vendor_invoice_date: string | null
  product_total_amount: string | number | null
  customer_orders_count: number
  created_at: string
}

interface VpoIndexProps {
  vpos: VpoRow[]
}

// ─── Helpers ────────────────────────────────────

// Map VendorPurchaseOrder.state enum keys → Catalyst Badge colors. Keep all
// known keys explicit so a new enum value renders a visible neutral pill
// instead of crashing the row.
const STATE_COLORS: Record<string, 'zinc' | 'indigo' | 'amber' | 'orange' | 'emerald' | 'red' | 'sky' | 'violet' | 'rose' | 'lime'> = {
  unconfirmed_vpo:    'amber',
  confirmed_vpo:      'sky',
  vendor_payments:    'violet',
  needs_routing:      'orange',
  shipped_from_brand: 'indigo',
  in_warehouse:       'emerald',
  scanned:            'lime',
  partially_scanned:  'amber',
  allocated:          'indigo',
  distributed:        'emerald',
  credit:             'rose',
  closed_win:         'emerald',
  closed_lost:        'zinc',
  vendor_po_issues:   'red',
}

function humanize(s: string | null): string {
  if (!s) return '—'
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function formatMoney(amount: string | number | null, currency: string | null): string {
  if (amount == null) return '—'
  const n = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(n)) return '—'
  const symbol = currency === 'EUR' ? '€' : currency === 'CHF' ? 'CHF ' : '$'
  return `${symbol}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// ─── Component ──────────────────────────────────

export default function VpoIndex({ vpos }: VpoIndexProps) {
  const columns: DataTableColumn<VpoRow>[] = [
    {
      key: 'vendor_po_number', label: 'VPO #',
      render: row => (
        <Link href={`/vpo/show/${row.id}`} className="font-medium text-indigo-600 hover:underline dark:text-indigo-400">
          {row.vendor_po_number || `#${row.id}`}
        </Link>
      ),
      sortValue: row => row.vendor_po_number || '',
      filterValue: row => row.vendor_po_number || '',
    },
    {
      key: 'vendor_name', label: 'Vendor',
      render: row => <>{row.vendor_name || '—'}</>,
      sortValue: row => row.vendor_name || '',
      filterValue: row => row.vendor_name || '',
    },
    {
      key: 'state', label: 'State',
      render: row => (
        row.state
          ? <Badge color={STATE_COLORS[row.state] || 'zinc'}>{humanize(row.state)}</Badge>
          : <span className="text-zinc-400">—</span>
      ),
      sortValue: row => row.state || '',
      filterValue: row => humanize(row.state),
    },
    {
      key: 'vendor_order_date', label: 'Order Date',
      render: row => <>{row.vendor_order_date || '—'}</>,
      sortValue: row => row.vendor_order_date || '',
      filterValue: row => row.vendor_order_date || '',
    },
    {
      key: 'vendor_invoice_number', label: 'Invoice #',
      render: row => <>{row.vendor_invoice_number || '—'}</>,
      sortValue: row => row.vendor_invoice_number || '',
      filterValue: row => row.vendor_invoice_number || '',
    },
    {
      key: 'product_total_amount', label: 'Total',
      align: 'right',
      render: row => <span className="font-medium tabular-nums">{formatMoney(row.product_total_amount, row.currency)}</span>,
      sortValue: row => Number(row.product_total_amount ?? 0),
    },
    {
      key: 'customer_orders_count', label: 'COs',
      align: 'right',
      render: row => (
        row.customer_orders_count > 0
          ? <Badge color="indigo">{row.customer_orders_count}</Badge>
          : <span className="text-zinc-400">—</span>
      ),
      sortValue: row => row.customer_orders_count,
    },
    {
      key: 'created_at', label: 'Created',
      render: row => <>{row.created_at}</>,
      sortValue: row => row.created_at,
      filterValue: row => row.created_at,
    },
  ]

  return (
    <>
      <Head title="Vendor POs" />

      <PageHeader
        title="Vendor POs"
        subtitle={`${vpos.length} ${vpos.length === 1 ? 'order' : 'orders'}`}
        icon={<ClipboardDocumentListIcon className="h-5 w-5" />}
        actions={
          <Link
            href="/vpo/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
            data-testid="new-vpo-button"
          >
            <PlusIcon className="h-4 w-4" />
            New VPO
          </Link>
        }
      />

      <DataTable
        rows={vpos}
        columns={columns}
        rowKey={row => row.id}
        emptyText="No vendor POs yet"
        filteredEmptyText="No vendor POs match your filters"
      />
    </>
  )
}

VpoIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
